import { frontendURL } from 'dashboard/helper/URLHelper';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import store from 'dashboard/store';
import AppContainer from 'dashboard/routes/dashboard/Dashboard.vue';
import SettingsContent from 'dashboard/routes/dashboard/settings/Wrapper.vue';
import pluginMetadata from '../../../plugin.json';
import TrayIntegration from './routes/settings/integrations/Tray.vue';
import TrayOrdersIndex from './routes/tray/TrayOrdersIndex.vue';
import TrayAbandonedCheckoutsIndex from './routes/tray/TrayAbandonedCheckoutsIndex.vue';

const automationContextKey = pluginMetadata.config.AUTOMATION_CONTEXT_KEY;
const automationContextLabel = pluginMetadata.config.AUTOMATION_CONTEXT_LABEL;
const automationContextIcon = pluginMetadata.config.AUTOMATION_CONTEXT_ICON;

const buildTextFilterOperators = () => [
  { value: 'equal_to', label: 'Equal to' },
  { value: 'not_equal_to', label: 'Not equal to' },
  { value: 'contains', label: 'Contains' },
  { value: 'does_not_contain', label: 'Does not contain' },
  { value: 'is_present', label: 'Is present' },
  { value: 'is_not_present', label: 'Is not present' },
];

const automationExtension = {
  entityAttributes: {
    contact: {
      tray_event_name: {
        name: 'TRAY_EVENT_NAME',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_scope_name: {
        name: 'TRAY_SCOPE_NAME',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_action: {
        name: 'TRAY_ACTION',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_order_id: {
        name: 'TRAY_ORDER_ID',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_order_number: {
        name: 'TRAY_ORDER_NUMBER',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_order_status: {
        name: 'TRAY_ORDER_STATUS',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_payment_method: {
        name: 'TRAY_PAYMENT_METHOD',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_total: {
        name: 'TRAY_TOTAL',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
      tray_currency: {
        name: 'TRAY_CURRENCY',
        inputType: 'plain_text',
        filterOperators: buildTextFilterOperators(),
      },
    },
  },
  eventContexts: {
    [automationContextKey]: {
      label: automationContextLabel,
      icon: automationContextIcon,
      events: pluginMetadata.config.AUTOMATION_EVENT_KEYS,
    },
  },
  eventEntities: pluginMetadata.config.AUTOMATION_EVENT_KEYS.reduce(
    (result, key) => ({
      ...result,
      [key]: ['contact'],
    }),
    {}
  ),
  actionsByEntity: {
    contact: [
      'send_webhook_event',
      'delay_next_action',
      'delay_next_action_range',
    ],
  },
  automationRuleEvents: pluginMetadata.config.AUTOMATION_EVENT_KEYS.map(
    key => ({
      key,
      value: key.toUpperCase(),
      context: automationContextKey,
    })
  ),
};

const ensurePluginState = () => {
  window.chatwoot = window.chatwoot || {};
  window.chatwoot.plugins = window.chatwoot.plugins || {};
  window.chatwoot.plugins.tray = window.chatwoot.plugins.tray || {};

  return window.chatwoot.plugins.tray;
};

const mergeAutomationRuleEvents = (baseEvents = [], extensionEvents = []) => {
  const seen = new Map();
  [...baseEvents, ...extensionEvents].forEach(event => {
    if (event?.key) {
      seen.set(event.key, event);
    }
  });

  return Array.from(seen.values());
};

const getTrayIntegration = () => {
  const integrationGetter = store.getters['integrations/getIntegration'];
  return integrationGetter ? integrationGetter('tray', {}) : null;
};

const hasActiveTrayConnection = () => {
  const integration = getTrayIntegration();
  return Boolean(
    Array.isArray(integration?.hooks) && integration.hooks.length > 0
  );
};

const registerIntegrationRoute = () => {
  const router = window.chatwootRouter;
  if (!router?.addRoute || router?.hasRoute?.('settings_integrations_tray')) {
    return;
  }

  router.addRoute({
    path: frontendURL('accounts/:accountId'),
    component: AppContainer,
    children: [
      {
        path: 'settings/integrations/tray',
        component: SettingsContent,
        props: {
          headerTitle: 'INTEGRATION_SETTINGS.HEADER',
          icon: 'flash-on',
          showBackButton: true,
          backUrl: { name: 'settings_applications' },
        },
        children: [
          {
            path: '',
            name: 'settings_integrations_tray',
            component: TrayIntegration,
            meta: {
              featureFlag: FEATURE_FLAGS.INTEGRATIONS,
              permissions: ['administrator'],
            },
            props: route => ({ error: route.query.error }),
          },
        ],
      },
      {
        path: 'tray/orders',
        name: 'tray_orders',
        component: TrayOrdersIndex,
        meta: {
          permissions: ['administrator'],
        },
      },
      {
        path: 'tray/carts',
        name: 'tray_abandoned_checkouts',
        component: TrayAbandonedCheckoutsIndex,
        meta: {
          permissions: ['administrator'],
        },
      },
    ],
  });
};

const rematchCurrentRoute = () => {
  const router = window.chatwootRouter;
  const currentRoute = router?.currentRoute?.value;
  if (!router?.replace || !currentRoute) {
    return;
  }

  const isGenericIntegrationRoute =
    currentRoute.name === 'settings_applications_integration' &&
    currentRoute.params?.integration_id === 'tray';
  const isTrayPath = currentRoute.path?.includes('/settings/integrations/tray');
  if (!isGenericIntegrationRoute && !isTrayPath) {
    return;
  }

  const accountId =
    currentRoute.params?.accountId || currentRoute.params?.account_id;
  if (!accountId) {
    return;
  }

  Promise.resolve().then(() =>
    router
      .replace({
        name: 'settings_integrations_tray',
        params: { accountId },
        query: currentRoute.query,
        hash: currentRoute.hash,
      })
      .catch(() => {})
  );
};

export default {
  install(app, { PluginSystem } = {}) {
    const pluginState = ensurePluginState();
    pluginState.config = pluginMetadata.config;

    registerIntegrationRoute();
    rematchCurrentRoute();

    if (PluginSystem?.registerHook) {
      const {
        contact: automationContactAttributes = {},
        ...automationEntityAttributes
      } = automationExtension.entityAttributes || {};

      PluginSystem.registerHook('automation-enhanced:config', config => {
        if (!hasActiveTrayConnection()) {
          return config;
        }

        return {
          ...config,
          entityAttributes: {
            ...config.entityAttributes,
            ...automationEntityAttributes,
            contact: {
              ...(config.entityAttributes?.contact || {}),
              ...automationContactAttributes,
            },
          },
          eventContexts: {
            ...config.eventContexts,
            ...automationExtension.eventContexts,
          },
          eventEntities: {
            ...config.eventEntities,
            ...automationExtension.eventEntities,
          },
          actionsByEntity: {
            ...config.actionsByEntity,
            ...automationExtension.actionsByEntity,
            contact: [
              ...new Set([
                ...(config.actionsByEntity?.contact || []),
                ...(automationExtension.actionsByEntity?.contact || []),
              ]),
            ],
          },
          automationRuleEvents: mergeAutomationRuleEvents(
            config.automationRuleEvents,
            automationExtension.automationRuleEvents
          ),
        };
      });

      PluginSystem.registerHook('sidebar:menu-items', payload => {
        if (!payload?.items || !Array.isArray(payload.items)) {
          return payload;
        }

        const integration = getTrayIntegration();
        if (
          !Array.isArray(integration?.hooks) ||
          integration.hooks.length === 0
        ) {
          return payload;
        }

        const exists = payload.items.some(item => item.name === 'Tray');
        if (exists) {
          return payload;
        }

        payload.items.push({
          name: 'Tray',
          label: payload.t?.('TRAY_PAGES.SIDEBAR_TITLE') || 'Tray',
          icon: 'i-lucide-shopping-cart',
          children: [
            {
              name: 'Tray Orders',
              label: payload.t?.('TRAY_PAGES.SIDEBAR_ORDERS') || 'Pedidos',
              icon: 'i-lucide-receipt-text',
              activeOn: ['tray_orders'],
              to: payload.accountScopedRoute('tray_orders'),
            },
            {
              name: 'Tray Carts',
              label: payload.t?.('TRAY_PAGES.SIDEBAR_ABANDONED') || 'Carrinhos',
              icon: 'i-lucide-shopping-basket',
              activeOn: ['tray_abandoned_checkouts'],
              to: payload.accountScopedRoute('tray_abandoned_checkouts'),
            },
          ],
        });

        return payload;
      });
    }

    app.component('TrayIntegration', TrayIntegration);
    app.component('TrayOrdersIndex', TrayOrdersIndex);
    app.component('TrayAbandonedCheckoutsIndex', TrayAbandonedCheckoutsIndex);

    store
      .dispatch('integrations/get')
      .then(() => {
        window.dispatchEvent(new Event('chatwoot:sidebar:refresh'));
      })
      .catch(() => {});
  },
};
